import { post, Delete } from '@/utils/https'

/** Vercel serverless body limit ~4.5 MB — multipart overhead requires a lower threshold. */
const DIRECT_UPLOAD_SIZE_THRESHOLD = 4 * 1024 * 1024

/**
 * Upload direct vers Supabase via URL signée (contourne la limite Vercel).
 */
async function uploadFileViaSignedUrl(file, config = {}) {
  const initResponse = await post(
    '/api/app/v1/storage/upload-init',
    {
      module: config.module || '',
      module_id: config.module_id,
      sub_module: config.sub_module,
      file_name: file.name,
      file_size: file.size,
      content_type: file.type || 'application/octet-stream',
    },
    {
      timeout: config.timeout ?? 30 * 1000,
      hideMessageModal: config.hideMessageModal === true,
    },
  )

  if (initResponse.code !== 200 || !initResponse.data) {
    throw new Error(initResponse.message || 'Upload init failed')
  }

  const { signedUrl, fileKey, bucketName, fileURL } = initResponse.data

  let uploadResponse
  try {
    uploadResponse = await fetch(signedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    })
  } catch (fetchError) {
    const networkError = new Error(fetchError?.message || 'Network Error')
    networkError.code = 'ERR_NETWORK'
    networkError.name = fetchError?.name || 'TypeError'
    throw networkError
  }

  if (!uploadResponse.ok) {
    const error = new Error(`Direct upload failed (${uploadResponse.status})`)
    if (uploadResponse.status === 413) {
      error.response = { status: 413 }
    }
    throw error
  }

  return {
    code: 200,
    data: {
      fileURL,
      fileKey,
      bucketName,
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type,
    },
    message: 'File uploaded successfully',
  }
}

/**
 * Upload un fichier vers Supabase Storage via l'API backend
 * Utilise la service role key pour contourner RLS (nécessaire pendant l'onboarding)
 *
 * @param {File} file - Le fichier à uploader
 * @param {Object} config - Configuration de l'upload
 * @param {string} config.module - Module name (e.g., 'legal-documents')
 * @param {string} config.module_id - Module record ID (e.g., user_id)
 * @param {string} [config.sub_module] - Sub-module name (optional)
 * @returns {Promise<Object>} Upload result with fileURL, fileKey, fileName
 */
export const uploadFileToStorage = (file, config = {}) => {
  if (file.size >= DIRECT_UPLOAD_SIZE_THRESHOLD) {
    return uploadFileViaSignedUrl(file, config)
  }

  const formData = new FormData()
  formData.append('file', file)
  formData.append('module', config.module || '')

  if (config.module_id) {
    formData.append('module_id', config.module_id)
  }

  if (config.sub_module) {
    formData.append('sub_module', config.sub_module)
  }

  return post('/api/app/v1/storage/upload', formData, {
    // Content-Type with boundary is set by the browser — never set multipart/form-data manually.
    timeout: config.timeout ?? 120 * 1000,
    hideMessageModal: config.hideMessageModal === true,
  })
}

/**
 * Supprime un fichier de Supabase Storage via l'API backend
 *
 * @param {string} fileKey - Le chemin du fichier dans le storage (ex: 'legal-documents/user_id/filename.pdf')
 * @param {string} bucketName - Le nom du bucket (ex: 'freelancer-documents')
 * @returns {Promise<Object>} Delete result
 */
export const deleteFileFromStorage = (fileKey, bucketName) => {
  // Encoder les paramètres pour l'URL
  const params = new URLSearchParams({
    fileKey,
    bucketName,
  })
  return Delete(`/api/app/v1/storage/delete?${params.toString()}`)
}
